import React from "react";
import { Box, Divider, Typography } from "@bca/ui-library";
import styled from "styled-components";
import { layout, LayoutProps } from "styled-system";

import SaleType from "./sales/SaleType";

interface SaleDateTimeProxyBidProps {
  startDate: string;
  status: string;
  saleLocation?: string;
}

const isToday = (date: Date) => {
  const today = new Date();

  return (
    date.getDate() == today.getDate() &&
    date.getMonth() == today.getMonth() &&
    date.getFullYear() == today.getFullYear()
  );
};

const isTomorrow = (date: Date) => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  return (
    date.getDate() == tomorrow.getDate() &&
    date.getMonth() == tomorrow.getMonth() &&
    date.getFullYear() == tomorrow.getFullYear()
  );
};

const getSaleDate = (saleDate: string) => {
  const weekday = new Date(saleDate).toLocaleString("en-GB", {
    weekday: "long",
  });
  const date = new Date(saleDate).toLocaleString("en-GB", {
    day: "2-digit",
  });
  const month = new Date(saleDate).toLocaleString("en-GB", {
    month: "long",
  });
  if (isToday(new Date(saleDate))) {
    return "Today";
  } else if (isTomorrow(new Date(saleDate))) {
    return "Tomorrow";
  }
  return `${weekday} ${date} ${month}`;
};

const getSaleTime = (saleDate: string) => {
  return `at ${new Date(saleDate).toLocaleTimeString("en-GB", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

const saleStartsAtMidnight = (startDate: string): boolean => {
  const saleTimeUTC = new Date(startDate).toLocaleTimeString("en-GB", {
    timeZone: "UTC",
    hour12: false,
  });
  return saleTimeUTC === "00:00:00";
};

const getProxyBidSaleDateAndTime = (status: string, startDate: string) => {
  if (saleStartsAtMidnight(startDate)) {
    return `Time and Sale sections subject to change`;
  }

  if (status === "Started") {
    return `Started ${getSaleTime(startDate)}`;
  }

  return `${getSaleDate(startDate)} ${getSaleTime(startDate)}`;
};

const getSaleStatus = (startDate: string, saleStatus: string) => {
  const saleTimeUTC = new Date(startDate).toLocaleTimeString("en-GB", {
    timeZone: "UTC",
    hour12: false,
  });
  const saleStartsAtMidnight = saleTimeUTC === "00:00:00";

  if (saleStartsAtMidnight) {
    return "Scheduled";
  } else if (saleStatus === "NotStarted") {
    return "Confirmed";
  } else {
    return "Started";
  }
};

const getSaleChannelLabel = (saleLocation?: string): string => {
  if (saleLocation === "Hybrid") return "Hybrid";
  return "Online Auction";
};

const DividerWithLayout = styled(Divider)<LayoutProps>`
  background-color: #7e9ab3;
  ${layout}
`;

const SaleDateTimeProxyBid: React.FunctionComponent<SaleDateTimeProxyBidProps> = ({
  startDate,
  status,
  saleLocation,
}) => {
  const saleChannelLabel = getSaleChannelLabel(saleLocation);
  return (
    <>
      <Box
        marginTop="14px"
        marginBottom={["20px", "18px"]}
        display="flex"
        flexDirection={["column", "row"]}
        alignItems={["flex-start", "center"]}
      >
        <SaleType
          saleInformationType={getSaleStatus(startDate, status)}
        ></SaleType>
        {saleStartsAtMidnight(startDate) && (
          <Typography variant="body" color="grey-blue" marginY="2px">
            {getProxyBidSaleDateAndTime(status, startDate)}
          </Typography>
        )}
        {!saleStartsAtMidnight(startDate) && (
          <Box display="flex" alignItems="center">
            <Typography variant="body" color="grey-blue" marginY="2px">
              {getProxyBidSaleDateAndTime(status, startDate)}
            </Typography>
            <>
              <DividerWithLayout
                orientation="vertical"
                width="2px"
                height="15px"
                marginTop={0}
                marginBottom={0}
                marginLeft={3}
                marginRight={3}
              ></DividerWithLayout>
              <Typography variant="body" color="grey-blue" margin={0}>
                {saleChannelLabel}
              </Typography>
            </>
          </Box>
        )}
      </Box>
      {saleStartsAtMidnight(startDate) && (
        <Box display="flex" flexDirection="row">
          <Typography variant="body" color="grey-blue" margin={0}>
            {getSaleDate(startDate)}
          </Typography>
          <DividerWithLayout
            orientation="vertical"
            width="2px"
            height="15px"
            marginTop={0}
            marginBottom={0}
            marginLeft={3}
            marginRight={3}
          ></DividerWithLayout>
          <Typography variant="body" color="grey-blue" margin={0}>
            {saleChannelLabel}
          </Typography>
        </Box>
      )}
    </>
  );
};

export default SaleDateTimeProxyBid;
